import { useCallback, useEffect, useState } from 'react';

export interface PinUser {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string | null;
  position: string | null;
  app_role: 'employe' | 'contremaitre' | 'manager' | 'admin';
}

export const usePinAuth = () => {
  const [pinUser, setPinUser] = useState<PinUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkPinAuth = useCallback(() => {
    try {
      const authMethod = localStorage.getItem('auth_method');
      const isAuth = localStorage.getItem('is_authenticated') === 'true';
      
      console.log('🔍 usePinAuth - Vérification de l\'authentification:');
      console.log('  - auth_method:', authMethod);
      console.log('  - is_authenticated:', isAuth);
      console.log('  - pin_user_id:', localStorage.getItem('pin_user_id'));
      console.log('  - pin_user_role:', localStorage.getItem('pin_user_role'));
      
      if (authMethod === 'pin' && isAuth) {
        const user: PinUser = {
          id: localStorage.getItem('pin_user_id') || '',
          name: localStorage.getItem('pin_user_name') || '',
          role: localStorage.getItem('pin_user_role') || 'employe',
          email: localStorage.getItem('pin_user_email') || '',
          phone: localStorage.getItem('pin_user_phone'),
          position: localStorage.getItem('pin_user_position'),
          app_role: (localStorage.getItem('pin_user_role') as 'employe' | 'contremaitre' | 'manager' | 'admin') || 'employe'
        };
        
        console.log('✅ Utilisateur PIN authentifié:', user);
        setPinUser(user);
        setIsAuthenticated(true);
      } else {
        console.log('❌ Utilisateur PIN non authentifié');
        setPinUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'auth PIN:', error);
      setPinUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkPinAuth();
  }, [checkPinAuth]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => checkPinAuth();
    window.addEventListener('storage', handler);
    window.addEventListener('pin-auth-changed', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('pin-auth-changed', handler);
    };
  }, [checkPinAuth]);

  const signOut = () => {
    // Nettoyer le localStorage
    localStorage.removeItem('pin_user_id');
    localStorage.removeItem('pin_user_name');
    localStorage.removeItem('pin_user_role');
    localStorage.removeItem('pin_user_email');
    localStorage.removeItem('pin_user_phone');
    localStorage.removeItem('pin_user_position');
    localStorage.removeItem('manager_pin');
    localStorage.removeItem('auth_method');
    localStorage.removeItem('is_authenticated');
    localStorage.removeItem('pin_auth_source');
    
    setPinUser(null);
    setIsAuthenticated(false);

    // Synchroniser les autres hooks `usePinAuth()` de la page
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('pin-auth-changed'));
    }
  };

  const hasRole = (role: 'employe' | 'contremaitre' | 'manager' | 'admin'): boolean => {
    if (!pinUser?.app_role) return false;
    
    switch (role) {
      case 'employe':
        return ['employe', 'contremaitre', 'manager', 'admin'].includes(pinUser.app_role);
      case 'contremaitre':
        return ['contremaitre', 'manager', 'admin'].includes(pinUser.app_role);
      case 'manager':
        return ['manager', 'admin'].includes(pinUser.app_role);
      case 'admin':
        return pinUser.app_role === 'admin';
      default:
        return false;
    }
  };

  const isManager = (): boolean => {
    return hasRole('manager') || hasRole('contremaitre') || hasRole('admin');
  };

  const isEmployee = (): boolean => {
    return hasRole('employe');
  };

  const getManagerPin = (): string | null => {
    return localStorage.getItem('manager_pin');
  };

  return {
    pinUser,
    loading,
    isAuthenticated,
    signOut,
    hasRole,
    isManager,
    isEmployee,
    getManagerPin,
    refetch: checkPinAuth,
  };
};
